import { createAsyncThunk } from "@reduxjs/toolkit";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import type { RootState } from "_shared/_stores/redux-store";
import type Guid from "_shared/guids/guid";
import * as Config from "config";
import { messagingApi } from "messaging/_api/messaging-api";
import MessageParameters, { MessageFilterParameters } from "messaging/_api/queries/message-parameters";
import MessageQuery from "messaging/_api/queries/message-query";
import type Message from "messaging/message";
import { isUnreadBy } from "messaging/read-receipts/unread-rule";

/**
 * Pages enough to reach any unread message a long conversation holds, and not so many that a count the
 * pages never reach keeps loading forever.
 */
const maximumPages = 100;

/**
 * Loads older pages of a forum's messages until every message the server counts as unread is loaded, or
 * there is nothing older, and answers with the id of the oldest unread one (#5828).
 *
 * The count is the server's, which takes in messages the pane never loaded. A reader with fifty unread
 * messages and a first page of twenty has thirty they cannot scroll to until these pages arrive.
 */
export const loadThroughOldestUnread = createAsyncThunk<
  string | undefined,
  { forumId: string; readerId: Guid },
  { state: RootState }
>("messages/loadThroughOldestUnread", async ({ forumId, readerId }, { dispatch, getState }) => {
  for (let page = 0; page < maximumPages; page++) {
    const state = getState();
    const loaded = state.messages.entries[forumId];
    const unread = (loaded?.data ?? []).filter((message) => isUnreadBy(message, readerId));
    const serverCount = state.forumUnreadCounts.entries[forumId] ?? unread.length;
    if (!loaded || unread.length >= serverCount || !loaded.hasNextPage) return oldest(unread)?.id.value;

    // The pages the chat itself loads, newest first, so the next page is the next older one (#6760).
    const query = new MessageQuery(
      new MessageParameters(new MessageFilterParameters({ newestFirst: true })),
      PaginationParameters.create({
        pageIndex: loaded.pageIndex + 1,
        pageSize: loaded.pageSize || Config.chatPageSize,
      }),
    );
    await dispatch(
      messagingApi.endpoints.getMessagesByForum.initiate({ forumId, query, append: true }, { subscribe: false }),
    ).unwrap();
  }
  const unread = (getState().messages.entries[forumId]?.data ?? []).filter((message) => isUnreadBy(message, readerId));
  return oldest(unread)?.id.value;
});

function oldest(messages: readonly Message[]): Message | undefined {
  let found: Message | undefined;
  for (const message of messages) {
    if (!found || (message.created && found.created && message.created.isBefore(found.created))) found = message;
  }
  return found;
}
